import type { Locale } from "@/lib/i18n";

export type ModuleIconKey =
  | "patient"
  | "calendar"
  | "treatment"
  | "finance"
  | "globe"
  | "box"
  | "people"
  | "health"
  | "chat"
  | "shield";

type L = Record<Locale, string>;

export type ModuleItem = {
  /** Kısa modül adı (eyebrow) */
  name: L;
  /** Vurgulu başlık (tagline) */
  headline: L;
  /** Zengin açıklama */
  desc: L;
  /** Kullandıkça öde (ek modül) */
  addon?: boolean;
  /** Yol haritasında (henüz canlı değil) */
  soon?: boolean;
};

export type ModuleGroup = {
  key: string;
  icon: ModuleIconKey;
  title: L;
  modules: ModuleItem[];
};

export type Highlight = { title: L; desc: L };

const MODULE_GROUPS_SOURCE: ModuleGroup[] = [
  {
    key: "clinical-flow",
    icon: "patient",
    title: { tr: "Hasta & Klinik Akışı", en: "Patient & Clinical Flow" },
    modules: [
      {
        name: { tr: "Randevu", en: "Appointments" },
        headline: {
          tr: "Kliniğinizin nabzı tek ekranda.",
          en: "Your clinic's pulse on one screen.",
        },
        desc: {
          tr: "Gelişmiş randevu modülü ile hekimlerin slotlarına tek tıkla Mola, Toplantı, Ara, Tatil girebilirsiniz. Takvim ve Pano görünümüyle klinik içi akışı canlı takip eder; hastanın randevu aldığı andan çıkışına kadar her adımı kayıt altına alır, durum geçişleriyle gelişmiş analizler üretirsiniz.",
          en: "Add Break, Meeting, Call or Holiday to a provider's slots in one click. Track the in-clinic flow live in Calendar and Board views; log every step from booking to check-out and produce advanced analytics from status transitions.",
        },
      },
      {
        name: { tr: "Muayene & Tedavi", en: "Examination & Treatment" },
        headline: {
          tr: "Diş şemasından tedavi planına, tüm süreç kayıt altında.",
          en: "From dental chart to treatment plan — the whole process on record.",
        },
        desc: {
          tr: "İnteraktif diş şeması (odontogram) üzerinden hastanın ağız durumunu görsel kaydeder, tedavi planlarını tek ekrandan oluşturursunuz. Anamnez, muayene bulguları, ICD tanıları ve tedavi adımları protokol akışıyla izlenebilir olur. Fiyatlandırma motoruyla entegre plan sayesinde her tedavi seçildiğinde fiyat otomatik hesaplanır.",
          en: "Record the patient's oral status on an interactive dental chart (odontogram) and build treatment plans from one screen. Anamnesis, findings, ICD diagnoses and treatment steps become traceable through the protocol flow. Integrated with the pricing engine, the price is calculated automatically whenever a treatment is selected.",
        },
      },
      {
        name: { tr: "AI Tedavi Asistanı", en: "AI Treatment Assistant" },
        addon: true,
        headline: {
          tr: "Sesli dikteden tedavi planına, saniyeler içinde.",
          en: "From voice dictation to a treatment plan in seconds.",
        },
        desc: {
          tr: "Muayene sırasında sesli dikte edersiniz; yapay zeka konuşmanızı diş + tedavi + fiyat olarak otomatik eşleştirip tedavi planına dönüştürür. Elle veri girişi azalır, hekim koltuktaki hastaya odaklanır.",
          en: "Dictate during the exam; AI automatically matches your speech to tooth + treatment + price and turns it into a treatment plan. Manual data entry drops, and the dentist stays focused on the patient in the chair.",
        },
      },
      {
        name: { tr: "3D Tedavi Planı", en: "3D Treatment Plan" },
        addon: true,
        headline: {
          tr: "Hasta planı kendi ağzında görsün.",
          en: "Let patients see the plan in their own mouth.",
        },
        desc: {
          tr: "Hekimin hazırladığı tedavi planı, hastanın kendi diş şeması üzerinde 3D ve adım adım oynar: çekim, implant, üst yapı — sırayla, gözünün önünde. Koltuk başında tablet ya da ikinci ekranda tam ekran sunum; fiyatlar siz açana kadar gizli kalır. Aynı sahne hasta portalında da açılır, hasta evde ailesine gösterebilir. Anlaşılan tedaviye \"evet\" demek kolaylaşır.",
          en: "The treatment plan the dentist prepares plays back in 3D, step by step, on the patient's own dental chart: extraction, implant, restoration — in order, right in front of them. Present it full screen on a tablet or second monitor at the chair, with prices hidden until you choose to show them. The same scene opens in the patient portal, so patients can show it to their family at home. Saying yes to a treatment they understand becomes easier.",
        },
      },
      {
        name: { tr: "Online Randevu", en: "Online Booking" },
        headline: {
          tr: "Web sitenizden 7/24 randevu.",
          en: "24/7 booking from your own website.",
        },
        desc: {
          tr: "Klinik grubu veya klinik bazında online randevu aracını web sitenize iframe/embed koduyla kolayca ekler, hemen randevu almaya başlarsınız. Hekimlerin çalışma gün/saatlerini, tatil ve molalarını tanımlar; kayıtlı/devam hastasına randevu verilip verilmeyeceğini ayarlarsınız. Otomatik oluşturma veya onay mekanizması seçebilir; CAPTCHA ve OTP doğrulaması ile bot ve sahte kayıtlara karşı korunursunuz.",
          en: "Add the booking tool to your website with an iframe/embed code — per clinic group or clinic — and start taking appointments immediately. Define providers' working hours, holidays and breaks; decide whether registered patients can book. Choose auto-creation or approval; protect against bots and fake records with CAPTCHA and OTP.",
        },
      },
      {
        name: { tr: "Hasta Portalı", en: "Patient Portal" },
        headline: {
          tr: "Hastanız kendi cebinden takip etsin.",
          en: "Let patients follow along from their own pocket.",
        },
        desc: {
          tr: "Hastalarınız kendi portallarından randevularını, tedavi planlarını ve ödemelerini görüntüler, online ödeme yapar. Güvenli tek-kullanımlık kod (OTP) girişiyle veriler korunur; telefon trafiğini azaltır, iletişimi dijitalleştirirsiniz.",
          en: "Patients view their appointments, treatment plans and payments from their own portal and pay online. Data is protected with secure one-time-code (OTP) login; you cut phone traffic and digitize communication.",
        },
      },
      {
        name: { tr: "Laboratuvar", en: "Laboratory" },
        headline: {
          tr: "Lab işleri gecikmesin, gözden kaçmasın.",
          en: "No lab job delayed or overlooked.",
        },
        desc: {
          tr: "Çalıştığınız laboratuvarları ve fiyat listelerini sisteme tanıtır, hastalarınızın işlerini laboratuvar modülü üzerinden uçtan uca takip edersiniz.",
          en: "Register the laboratories you work with and their price lists, and track your patients' jobs end-to-end through the laboratory module.",
        },
      },
    ],
  },
  {
    key: "experience-crm",
    icon: "chat",
    title: { tr: "Hasta Deneyimi & CRM", en: "Patient Experience & CRM" },
    modules: [
      {
        name: { tr: "Anketler", en: "Surveys" },
        headline: {
          tr: "Doğru soruyu, doğru kişiye, doğru anda.",
          en: "The right question, to the right person, at the right moment.",
        },
        desc: {
          tr: "Sınırsız sayıda, tamamen dinamik anket oluşturur; dilediğiniz gün/saatte, dilediğiniz hedef kitleye gönderirsiniz. “Randevu sonrası gönder”, “Tedavi sonrası gönder” gibi tetikleyiciler tanımlarsınız.",
          en: "Create unlimited, fully dynamic surveys and send them to any target audience on any day and time. Define triggers such as “send after appointment” or “send after treatment.”",
        },
      },
      {
        name: { tr: "Şikayet Yönetimi", en: "Complaint Management" },
        headline: {
          tr: "Memnuniyetsizliği fırsat kaçmadan yakalayın.",
          en: "Catch dissatisfaction before the opportunity is lost.",
        },
        desc: {
          tr: "Memnuniyet anketlerindeki kritik sorulara eşik değer belirlersiniz; hasta bu değerin altında puan verdiğinde otomatik şikayet kaydı oluşur. Manuel kayıt da açabilir, sorumlulara görev atayıp çözüm durumunu anlık izlersiniz.",
          en: "Set threshold values for critical survey questions; when a patient scores below that value, a complaint record is created automatically. You can also open records manually, assign tasks to owners and track resolution in real time.",
        },
      },
      {
        name: { tr: "Destek / Ticket", en: "Support / Tickets" },
        addon: true,
        headline: {
          tr: "Klinik içi süreçler dijital ve izlenebilir.",
          en: "In-clinic processes, digital and traceable.",
        },
        desc: {
          tr: "Dinamik formlarla (Ödeme İade, Arıza vb.) süreçlerinizi dijitalleştirir, taleplerin otomatik olarak ilgili birimlere düşmesini sağlarsınız. Mail/WhatsApp/telefon trafiğini azaltır, görevlere dair detaylı raporlar alırsınız.",
          en: "Digitize your processes with dynamic forms (Payment Refund, Fault, etc.) and route requests automatically to the relevant units. Reduce email/WhatsApp/phone traffic and get detailed task reports.",
        },
      },
      {
        name: { tr: "CRM", en: "CRM" },
        addon: true,
        headline: {
          tr: "Reklamdan koltuğa: tek ekranda lead, mesaj ve operasyon.",
          en: "From ad to chair: leads, messages and operations on one screen.",
        },
        desc: {
          tr: "Facebook, Instagram, Google'dan gelen reklam verilerini (Lead) otomatik toplar, kaynağa göre kategorize eder, doğru kişiye yönlendirirsiniz. Etkinlikler ekranında tüm iletişimi adım adım kaydeder; WhatsApp, Instagram, Telegram görüşmelerinizi CRM üzerinden yürütürsünüz. Yurt dışı hastalarınızın uçak, transfer, konaklama ve konsültasyon adımlarını Operasyon Ajandası'ndan takip eder, çok dilli Proforma Teklif ile detaylı tedavi planı hazırlarsınız.",
          en: "Automatically collect ad leads from Facebook, Instagram and Google, categorize by source and route to the right person. Log all communication step by step in the Activities screen; run your WhatsApp, Instagram and Telegram conversations through the CRM. Track international patients' flight, transfer, accommodation and consultation steps from the Operations Agenda, and prepare detailed treatment plans with multi-language Proforma Quotes.",
        },
      },
    ],
  },
  {
    key: "operations",
    icon: "box",
    title: { tr: "Klinik Operasyon", en: "Clinic Operations" },
    modules: [
      {
        name: { tr: "Stok", en: "Inventory" },
        headline: {
          tr: "Miadı da, lotu da, kritik seviyesi de sizden habersiz geçmez.",
          en: "Expiry, lot and critical level — nothing slips past you.",
        },
        desc: {
          tr: "Sarf malzeme, ilaç ve tıbbi cihaz envanteri. FEFO ile lot bazlı takip, İTS/ÜTS bildirim altyapısı, depolar arası talep/transfer, min-max seviyeler, miadı yaklaşan ve kritik stok, sayım.",
          en: "Consumables, medicine and medical-device inventory. Lot-based tracking with FEFO, İTS/ÜTS notification infrastructure, inter-warehouse requests/transfers, min-max levels, near-expiry and critical stock, and counts.",
        },
      },
      {
        name: { tr: "OraSync", en: "OraSync" },
        addon: true,
        headline: {
          tr: "Röntgen cihazından hasta dosyasına, otomatik köprü.",
          en: "An automatic bridge from X-ray device to patient file.",
        },
        desc: {
          tr: "Görüntüleme cihazlarınızdan çıkan röntgenleri OraSync köprüsü otomatik yakalar ve doğru hastanın dosyasına aktarır. Elle dosya taşıma, yanlış hastaya kayıt ve kayıp görüntü derdi biter.",
          en: "OraSync automatically captures X-rays from your imaging devices and files them to the right patient. No more manual file transfers, wrong-patient records or lost images.",
        },
      },
      {
        name: { tr: "Demirbaş", en: "Assets" },
        addon: true,
        headline: {
          tr: "Her ekipmanın sahibi, bakımı ve amortismanı belli.",
          en: "Every asset's owner, maintenance and depreciation is clear.",
        },
        desc: {
          tr: "Klinik ekipman, donanım, mobilya ve araç yönetimi. Zimmet, bakım, garanti ve VUK 313 amortisman takibi.",
          en: "Manage clinic equipment, hardware, furniture and vehicles. Assignment, maintenance, warranty and VUK-313 depreciation tracking.",
        },
      },
      {
        name: { tr: "Doküman Yönetimi", en: "Document Management" },
        addon: true,
        headline: {
          tr: "Prosedürler versiyonlu, onaylı ve okundu bilgili.",
          en: "Procedures — versioned, approved and read-tracked.",
        },
        desc: {
          tr: "Form, prosedür, politika ve iş akışı belgelerini oluşturur, personele okuması için atar, revizyonları kayıt altına alır ve onay akışları ile birim/yönetici onayına sunarsınız.",
          en: "Create form, procedure, policy and workflow documents, assign them to staff to read, log revisions, and submit them to unit/manager approval with approval flows.",
        },
      },
      {
        name: { tr: "Toplantı Yönetimi", en: "Meeting Management" },
        addon: true,
        headline: {
          tr: "Karar alınır, aksiyon takip edilir, tutanak kendiliğinden gider.",
          en: "Decisions made, actions tracked, minutes sent automatically.",
        },
        desc: {
          tr: "Toplantıları planlar; gündem, karar ve aksiyon takibi yaparsınız. Sonunda otomatik oluşan tutanaklar katılımcılara gider. Kalite toplantıları düzenler, ilgili belgelerin gözden geçirilmesini sağlar, Karar Arşivi ile geçmiş kararlara tek ekrandan erişirsiniz.",
          en: "Plan meetings; track agenda, decisions and actions. Auto-generated minutes are sent to participants afterward. Run quality meetings, ensure documents are reviewed, and reach past decisions from one screen with the Decision Archive.",
        },
      },
      {
        name: { tr: "Sözleşme & Belge", en: "Contracts & Documents" },
        addon: true,
        headline: {
          tr: "Hiçbir ruhsat, hiçbir sözleşme süresini kaçırmayın.",
          en: "Never miss a license or a contract deadline.",
        },
        desc: {
          tr: "Sözleşmeleri, resmi ruhsat ve belgeleri yönetir; başlangıç/bitiş/yenileme durumlarını takip edersiniz. Bildirimlerle yasal süreler dolmadan yenileme yaparsınız.",
          en: "Manage contracts, official licenses and documents; track start/end/renewal states. With notifications, renew before legal deadlines expire.",
        },
      },
    ],
  },
  {
    key: "hr",
    icon: "people",
    title: { tr: "İnsan Kaynakları", en: "Human Resources" },
    modules: [
      {
        name: { tr: "İnsan Kaynakları", en: "Human Resources" },
        headline: {
          tr: "Özlükten performansa, self-servis İK.",
          en: "From personnel files to performance — self-service HR.",
        },
        desc: {
          tr: "Çalışanların özlük dosyalarını Oravity'den yönetir, izinleri onay mekanizmasıyla alırsınız. Manuel veya sistem verisinden KPI atar; 360° Performans Değerlendirme (yönetici, meslektaş, ast, İK) ve çalışan memnuniyet anketleriyle performans takibi yapar, PDF karne üretirsiniz. Çalışanlara sınırsız dinamik anket gönderirsiniz.",
          en: "Manage employees' personnel files in Oravity and handle leave through an approval mechanism. Assign KPIs manually or from system data; track performance with 360° reviews (manager, peer, subordinate, HR) and satisfaction surveys, and produce a PDF scorecard. Send unlimited dynamic surveys to employees.",
        },
      },
      {
        name: { tr: "Akademi", en: "Academy" },
        addon: true,
        headline: {
          tr: "Oryantasyon ve gelişim tek platformda.",
          en: "Onboarding and development on one platform.",
        },
        desc: {
          tr: "Dinamik kurslar oluşturur; video ve dersler ekler, çalışanlara sınavlar atarsınız. Oryantasyon ve mesleki gelişimi Akademi modülüyle kolayca yönetirsiniz.",
          en: "Create dynamic courses; add videos and lessons and assign exams to employees. Manage onboarding and professional development easily with the Academy module.",
        },
      },
    ],
  },
  {
    key: "finance-ehealth",
    icon: "finance",
    title: { tr: "Finans & E-Sağlık", en: "Finance & E-Health" },
    modules: [
      {
        name: { tr: "Finans", en: "Finance" },
        headline: {
          tr: "Sınırsız fiyat kuralı, otomatik hakediş, mevzuata uygun fatura.",
          en: "Unlimited pricing rules, automatic commissions, compliant invoicing.",
        },
        desc: {
          tr: "Gelişmiş fiyatlandırma motoruyla sınırsız fiyat kuralı yazar; hekimlere hakediş şablonları tanımlar, detaylı hekim hakedişi raporlarsınız. Ödeme kampanyaları oluşturur, Iyzico, PayTR gibi sağlayıcılarla ödeme alır, e-Fatura/e-Arşiv entegratörleriyle yasal fatura kesersiniz. Banka/POS ayarları, dinamik POS komisyonu ve POS yaşlandırma raporları; TCMB kuruna ilave oran veya kendi kurunuzu tanımlarsınız.",
          en: "Write unlimited pricing rules with the advanced pricing engine; define provider commission templates and report detailed commissions. Create payment campaigns, take payments with providers like Iyzico and PayTR, and issue legal invoices via e-Invoice/e-Archive integrators. Bank/POS settings, dynamic POS commissions and POS aging reports; define an added rate over the TCMB rate or your own exchange rate.",
        },
      },
      {
        name: { tr: "E-Sağlık (e-Reçete & e-Nabız)", en: "E-Health (e-Prescription & e-Nabız)" },
        headline: {
          tr: "Mevzuata uyumlu, muayeneden tek tıkla.",
          en: "Regulation-compliant, one click from the exam.",
        },
        desc: {
          tr: "Reçetem (e-Reçete) entegrasyonu ile muayene ekranından tek tıkla e-reçete oluşturur; e-Nabız / SağlıkNet bildirim süreçlerinizi yürütürsünüz. Şube bazında e-sağlık ayarları ve şifreli kimlik yönetimiyle güvenle çalışırsınız.",
          en: "With the Reçetem (e-Prescription) integration, create an e-prescription in one click from the examination screen; run your e-Nabız / SağlıkNet notification processes. Work securely with branch-based e-health settings and encrypted credential management.",
        },
      },
      {
        name: { tr: "Raporlama & Analiz", en: "Reporting & Analytics" },
        headline: {
          tr: "Kararlarınızı hisle değil, veriyle verin.",
          en: "Decide with data, not gut feeling.",
        },
        desc: {
          tr: "Randevu, tahsilat, hekim performansı, stok ve daha fazlası için hazır ve kaydedilebilir raporlar. Dinamik filtrelerle klinik verinizi anlık analiz edersiniz.",
          en: "Ready-made and savable reports for appointments, collections, provider performance, stock and more. Analyze your clinical data instantly with dynamic filters.",
        },
      },
    ],
  },
  {
    key: "admin-security",
    icon: "shield",
    title: { tr: "Yönetim & Güvenlik", en: "Administration & Security" },
    modules: [
      {
        name: { tr: "Çok Şube, Rol & Yetki", en: "Multi-Branch, Roles & Permissions" },
        headline: {
          tr: "Tek klinikten franchise'a, aynı platform.",
          en: "From a single clinic to a franchise, the same platform.",
        },
        desc: {
          tr: "Şirket → Şube → Kullanıcı hiyerarşisi ve ayrıntılı rol/yetki sistemi ile ölçeklenir. Her kullanıcı yalnız yetkili olduğu veriyi görür; şube bazlı ayarlar ve çapraz-şube erişim kontrolü ile büyük yapıları güvenle yönetirsiniz.",
          en: "Scale with a Company → Branch → User hierarchy and a detailed role/permission system. Each user sees only the data they're authorized for; manage large structures safely with branch-based settings and cross-branch access control.",
        },
      },
      {
        name: { tr: "Güvenlik & KVKK", en: "Security & KVKK" },
        headline: {
          tr: "Verisi güvende olmayan bir sağlık yazılımı, yazılım değildir.",
          en: "A health app whose data isn't safe is not software.",
        },
        desc: {
          tr: "Çok kiracılı (multi-tenant) mimaride her kliniğin verisi birbirinden yalıtılır. Hasta dosyaları ve hassas veriler AES-256 ile şifreli saklanır, dosya erişimleri firma bazında izole edilir. KVKK uyumlu veri yönetimi altyapısı.",
          en: "In a multi-tenant architecture each clinic's data is isolated from the others. Patient files and sensitive data are stored encrypted with AES-256, and file access is isolated per company. KVKK-compliant data management infrastructure.",
        },
      },
    ],
  },
];

/**
 * Kodda hazır ama HENÜZ SATIŞA AÇILMAMIŞ modüller (TR slug).
 * Vitrinin tek kapısı burası: bu slug'lar `MODULE_GROUPS` dışında kalır, dolayısıyla
 * navigasyonda, footer'da, modül hub'ında, sitemap'te ve llms.txt'te görünmez;
 * detay sayfası da 404 döner (findModuleBySlug null verir).
 * Modül canlıya çıkınca (fiyat ucunun paidModules listesine düştüğünde) slug buradan silinir.
 */
const PENDING_MODULE_SLUGS = new Set<string>();

/** Modül henüz satışa açılmadı mı? (TR adından türetilen slug'a bakar) */
export function isModulePending(m: ModuleItem): boolean {
  return PENDING_MODULE_SLUGS.has(slugifyModule(m.name.tr));
}

/** Vitrine çıkan modül aileleri — satışa açılmamış modüller ayıklanmıştır. */
export const MODULE_GROUPS: ModuleGroup[] = MODULE_GROUPS_SOURCE.map((g) => ({
  ...g,
  modules: g.modules.filter((m) => !isModulePending(m)),
})).filter((g) => g.modules.length > 0);

/** Rakiplerde nadir — manşete çıkanlar */
export const HIGHLIGHTS: Highlight[] = [
  {
    title: { tr: "Sağlık turizmi paketi", en: "Health tourism suite" },
    desc: {
      tr: "CRM + döviz + çok dil + ülke bazlı lead — tek pakette.",
      en: "CRM + FX + multi-language + country-based leads in one suite.",
    },
  },
  {
    title: { tr: "Kural-motoru fiyatlandırma", en: "Rule-engine pricing" },
    desc: {
      tr: "Düz liste değil; TDB/SUT/formül ile dinamik fiyat.",
      en: "Not a flat list; dynamic pricing via TDB/SUT/formulas.",
    },
  },
  {
    title: { tr: "Online randevu widget", en: "Online booking widget" },
    desc: {
      tr: "Sitene göm; CAPTCHA + OTP + rate-limit ile güvenli.",
      en: "Embed on your site; secured with CAPTCHA + OTP + rate-limit.",
    },
  },
  {
    title: { tr: "Türkiye e-sağlık üçlüsü", en: "Turkey e-health trio" },
    desc: {
      tr: "e-Reçete + e-Nabız + e-Fatura tek çatı altında.",
      en: "e-Prescription + e-Nabız + e-Invoice under one roof.",
    },
  },
  {
    title: { tr: "Gerçek çok-şube / çok-kiracı", en: "True multi-branch / multi-tenant" },
    desc: {
      tr: "KVKK firma-bazlı dosya izolasyonuyla franchise'a hazır.",
      en: "Franchise-ready with KVKK per-company file isolation.",
    },
  },
  {
    title: { tr: "Modüler / kullandıkça öde", en: "Modular / pay-as-you-go" },
    desc: {
      tr: "Sadece ihtiyacın olan ek modüllere abone ol.",
      en: "Subscribe only to the add-ons you need.",
    },
  },
];

/** URL-güvenli slug (Türkçe karakterler dahil) */
export function slugifyModule(text: string): string {
  return text
    .toLowerCase()
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function moduleSlug(m: ModuleItem, locale: Locale): string {
  return slugifyModule(m.name[locale]);
}

/** Modüller kök yolu: /moduller (tr) · /en/modules (en) */
export function modulesPath(locale: Locale): string {
  return locale === "en" ? "/en/modules" : "/moduller";
}
export function modulePath(locale: Locale, slug: string): string {
  return `${modulesPath(locale)}/${slug}`;
}

export type FlatModule = { module: ModuleItem; group: ModuleGroup };

/** Tüm modüller (grubuyla birlikte) düz liste */
export const ALL_MODULES: FlatModule[] = MODULE_GROUPS.flatMap((group) =>
  group.modules.map((module) => ({ module, group })),
);

export function findModuleBySlug(slug: string, locale: Locale): FlatModule | null {
  return ALL_MODULES.find((fm) => moduleSlug(fm.module, locale) === slug) ?? null;
}

export const MODULE_ICON_PATHS: Record<ModuleIconKey, string> = {
  patient: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0",
  calendar:
    "M4.5 6.5A1.5 1.5 0 0 1 6 5h12a1.5 1.5 0 0 1 1.5 1.5V19a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V6.5ZM4.5 9.5h15M8 3.5v3M16 3.5v3",
  treatment: "M4 13V5a1 1 0 0 1 1-1h8l7 7-9 9-7-7Zm4.5-5.5h.01",
  finance:
    "M3.5 8.5A1.5 1.5 0 0 1 5 7h13a1 1 0 0 1 1 1v8.5a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 3.5 16.5v-8ZM15 12h2M3.5 8.5V6.5A1.5 1.5 0 0 1 5 5h11",
  globe:
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.5 12h17M12 3c2.4 2.5 3.6 5.7 3.6 9s-1.2 6.5-3.6 9c-2.4-2.5-3.6-5.7-3.6-9S9.6 5.5 12 3Z",
  box: "M12 3 3.5 7.5v9L12 21l8.5-4.5v-9L12 3ZM3.5 7.5 12 12l8.5-4.5M12 12v9",
  people:
    "M9 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7-1a3 3 0 1 0 0-6M3 20a6 6 0 0 1 12 0m2 0a5.5 5.5 0 0 0-3-4.9",
  health: "M3 12h3.5l2-5 3 11 2.5-6H21",
  chat: "M7.5 9h9M7.5 12.5h6M21 11.5a7.5 7.5 0 0 1-11 6.6L4 20l1.4-4.2A7.5 7.5 0 1 1 21 11.5Z",
  shield: "M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Zm-2.5 8.5 2 2 3.5-4",
};
